import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw, Loader2, User, Phone, Mail, Copy, ExternalLink,
  ChevronDown, Check, History, Wallet, Settings, Download,
  MessageCircle, Shield, Play, Square, Bot, Link2, Users, Calendar, Trash2
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

// Component for user row with profile selector
const UserRow = ({ user, profiles, onUpdate, onDelete, index, adminIds }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [assignedProfile, setAssignedProfile] = useState(null);

  const isAdmin = adminIds.includes(user.user_id);

  useEffect(() => {
    if (user.assigned_profile_id && profiles.length > 0) {
      const profile = profiles.find(p => (p.id || p._id) === user.assigned_profile_id);
      setAssignedProfile(profile);
    }
  }, [user.assigned_profile_id, profiles]);

  const assignProfile = async (profile) => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/tg-bot/users/${user.user_id}/profile`, {
        profile_id: profile ? (profile.id || profile._id) : null
      }, { headers: getAuthHeaders() });
      setAssignedProfile(profile);
      setIsOpen(false);
      toast.success(profile ? `Профиль назначен: ${profile.accountName || profile.name}` : 'Профиль удалён');
      onUpdate();
    } catch (error) {
      toast.error("Ошибка назначения профиля");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isAdmin) {
      toast.error("Нельзя удалить администратора");
      return;
    }
    if (!window.confirm(`Удалить пользователя ${user.first_name}?`)) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/tg-bot/users/${user.user_id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Пользователь удалён");
      onDelete(user.user_id);
    } catch (error) {
      toast.error("Ошибка удаления");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <tr className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-slate-50' : ''}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
            isAdmin ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-blue-500 to-purple-600'
          }`}>
            {(user.first_name || 'U').charAt(0)}
          </div>
          <div>
            <div className="font-medium text-slate-800 flex items-center gap-2">
              {user.first_name} {user.last_name || ''}
              {isAdmin && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Admin</span>}
            </div>
            {user.username && (
              <div className="text-sm text-slate-500">@{user.username}</div>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <code className="text-sm bg-slate-100 px-2 py-1 rounded">{user.user_id}</code>
      </td>
      <td className="py-3 px-4">
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            disabled={isSaving}
            className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 min-w-[200px] ${
              assignedProfile 
                ? 'bg-green-50 border-green-200 text-green-700' 
                : 'bg-white border-slate-200 text-slate-500'
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : assignedProfile ? (
              <>
                <User className="w-4 h-4" />
                <span className="truncate">{assignedProfile.accountName || assignedProfile.name}</span>
              </>
            ) : (
              <>
                <User className="w-4 h-4" />
                <span>Не назначен</span>
              </>
            )}
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isOpen && (
            <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
              <button
                onClick={() => assignProfile(null)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 text-slate-500 border-b"
              >
                ✕ Убрать профиль
              </button>
              {profiles.map((profile) => (
                <button
                  key={profile._id || profile.id}
                  onClick={() => assignProfile(profile)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                    assignedProfile && (assignedProfile.id || assignedProfile._id) === (profile.id || profile._id)
                      ? 'bg-green-50 text-green-700'
                      : 'text-slate-700'
                  }`}
                >
                  <User className="w-4 h-4 text-slate-400" />
                  <div className="flex-1 truncate">
                    <div>{profile.accountName || profile.name}</div>
                    <div className="text-xs text-slate-400">{(profile.template_balance || profile.balance || 0).toLocaleString()} ₽</div>
                  </div>
                  {assignedProfile && (assignedProfile.id || assignedProfile._id) === (profile.id || profile._id) && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-slate-600">
        {user.last_visit ? new Date(user.last_visit).toLocaleString('ru-RU') : '-'}
      </td>
      <td className="py-3 px-4">
        {!isAdmin && (
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            title="Удалить пользователя"
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        )}
      </td>
    </tr>
  );
};

const PAGES = [
  { id: 'wallet', name: 'Кошелёк', icon: Wallet, description: 'Балансы и активы' },
  { id: 'history', name: 'История', icon: History, description: 'Операции' },
  { id: 'verification', name: 'Верификация', icon: Shield, description: 'Статус KYC' },
  { id: 'profile', name: 'Профиль', icon: User, description: 'Данные пользователя' }
];

export default function TGCointPayTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedPage, setSelectedPage] = useState('wallet');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addRandomOps, setAddRandomOps] = useState(true);
  const [showBrowserFrame, setShowBrowserFrame] = useState(true);
  const [isVerified, setIsVerified] = useState(true);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  
  // Bot management state
  const [activeTab, setActiveTab] = useState('bot'); // 'generator', 'bot', or 'users'
  const [botStatus, setBotStatus] = useState({ is_running: false });
  const [botSettings, setBotSettings] = useState({
    bot_link: 'https://t.me/cointvault_bot',
    welcome_message: '',
    webapp_enabled: true,
    verified_default: true,
  });
  const [botProfileDropdown, setBotProfileDropdown] = useState(false);
  const [isSavingBot, setIsSavingBot] = useState(false);
  const [selectedBotProfile, setSelectedBotProfile] = useState(null);
  
  // Users state
  const [botUsers, setBotUsers] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, today: 0, this_week: 0 });

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      const profilesWithIds = response.data.map(profile => ({
        ...profile,
        visibleId: profile.visibleId || Math.floor(1000000 + Math.random() * 9000000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
    fetchBotStatus();
    fetchBotSettings();
    fetchBotUsers();
  }, [fetchProfiles]);

  const fetchBotStatus = async () => {
    try {
      const response = await axios.get(`${API}/tg-bot/status`, { headers: getAuthHeaders() });
      setBotStatus(response.data);
    } catch (error) {
      console.error("Error fetching bot status:", error);
    }
  };

  const fetchBotSettings = async () => {
    try {
      const response = await axios.get(`${API}/tg-bot/settings`, { headers: getAuthHeaders() });
      setBotSettings({
        bot_link: response.data.bot_link || 'https://t.me/cointvault_bot',
        welcome_message: response.data.welcome_message || '',
        webapp_enabled: response.data.webapp_enabled !== false,
        verified_default: response.data.verified_default !== false,
        active_profile_id: response.data.active_profile_id,
      });
      // Find and set selected profile
      if (response.data.active_profile_id && profiles.length > 0) {
        const activeProfile = profiles.find(p => 
          (p.id || p._id) === response.data.active_profile_id
        );
        if (activeProfile) setSelectedBotProfile(activeProfile);
      }
    } catch (error) {
      console.error("Error fetching bot settings:", error);
    }
  };

  const fetchBotUsers = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/tg-bot/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tg-bot/users/stats`, { headers: getAuthHeaders() }),
      ]);
      setBotUsers(usersRes.data);
      setUserStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching bot users:", error);
    }
  };

  const startBot = async () => {
    try {
      await axios.post(`${API}/tg-bot/start`, {}, { headers: getAuthHeaders() });
      toast.success("Бот запущен!");
      fetchBotStatus();
    } catch (error) {
      toast.error("Ошибка запуска бота");
    }
  };

  const stopBot = async () => {
    try {
      await axios.post(`${API}/tg-bot/stop`, {}, { headers: getAuthHeaders() });
      toast.success("Бот остановлен");
      fetchBotStatus();
    } catch (error) {
      toast.error("Ошибка остановки бота");
    }
  };

  const saveBotSettings = async () => {
    setIsSavingBot(true);
    try {
      await axios.post(`${API}/tg-bot/settings`, botSettings, { headers: getAuthHeaders() });
      toast.success("Настройки сохранены!");
    } catch (error) {
      toast.error("Ошибка сохранения настроек");
    } finally {
      setIsSavingBot(false);
    }
  };

  const setActiveProfile = async (profile) => {
    const newSettings = { ...botSettings, active_profile_id: profile.id || profile._id };
    setBotSettings(newSettings);
    setSelectedBotProfile(profile);
    setBotProfileDropdown(false);
    
    try {
      await axios.post(`${API}/tg-bot/settings`, newSettings, { headers: getAuthHeaders() });
      toast.success(`Активный профиль: ${profile.accountName || profile.name}`);
      fetchBotStatus();
    } catch (error) {
      toast.error("Ошибка установки профиля");
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const parseD = (d, t) => {
    if (!d) return 0;
    const p = d.split('.');
    if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}T${t || '00:00'}`).getTime();
    return 0;
  };

  const fmtDate = (ts) => {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  };

  const generateTransactions = (sourceTransactions) => {
    const result = [];
    
    // INVERT: OZON deposit → Cashinout withdrawal, OZON withdrawal → Cashinout deposit
    const sorted = [...(sourceTransactions || [])].sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));
    
    for (const tx of sorted) {
      const isOzonDeposit = tx.type === 'deposit';
      result.push({
        type: isOzonDeposit ? 'withdrawal' : 'deposit',
        amount: Math.abs(parseFloat(tx.amount || 0)),
        date: tx.date,
        time: tx.time || `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
      });
    }

    if (addRandomOps) {
      const dates = result.map(t => parseD(t.date, t.time)).filter(d => d > 0);
      const minT = dates.length > 0 ? Math.min(...dates) : Date.now() - 30*86400000;
      const maxT = dates.length > 0 ? Math.max(...dates) : Date.now();
      const count = Math.floor(Math.random() * 4) + 3;

      for (let i = 0; i < count; i++) {
        const t = new Date(minT + Math.random() * (maxT - minT));
        const isDeposit = Math.random() > 0.45;
        result.push({
          type: isDeposit ? 'deposit' : 'withdrawal',
          amount: Math.floor(Math.random() * 12000) + 500,
          date: fmtDate(t.getTime()),
          time: `${String(Math.floor(Math.random()*14)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
        });
      }

      // First deposit
      const oldestDate = new Date(minT);
      oldestDate.setDate(oldestDate.getDate() - Math.floor(Math.random() * 5) - 2);
      result.push({
        type: 'deposit',
        amount: Math.floor(Math.random() * 4000) + 2000,
        date: fmtDate(oldestDate.getTime()),
        time: `${String(Math.floor(Math.random()*12)+8).padStart(2,'0')}:${String(Math.floor(Math.random()*60)).padStart(2,'0')}`,
      });
    }

    result.sort((a, b) => parseD(b.date, b.time) - parseD(a.date, a.time));
    return result;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.accountName?.split(' ')[0] || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.accountName?.split(' ')[1] || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email && selectedProfile.email !== "не установлен" 
      ? selectedProfile.email 
      : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    const balance = selectedProfile.template_balance || selectedProfile.balance || 50000;
    const usdtBalance = (balance / 90).toFixed(2);
    const btcBalance = (parseFloat(usdtBalance) / 65000).toFixed(6);
    
    const transactions = generateTransactions(selectedProfile.transactions || selectedProfile.operations);
    
    const data = {
      visibleId: selectedProfile.visibleId,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      username: `@${firstName.toLowerCase()}${selectedProfile.visibleId || ''}`,
      phone: formatPhone(phone),
      email,
      balance: balance.toString(),
      usdtBalance,
      btcBalance,
      verified: isVerified,
      transactions,
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    const frameParam = showBrowserFrame ? '1' : '0';
    return `${window.location.origin}/templates/tg-cashinout/preview?data=${dataStr}&page=${selectedPage}&frame=${frameParam}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) window.open(url, '_blank');
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    try {
      const response = await axios.post(
        `${API}/templates/tg-cashinout/archive`,
        { 
          data: generatedData, 
          pages: ['wallet', 'history', 'verification', 'profile'],
          showFrame: showBrowserFrame
        },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TG_Cashinout_${generatedData.visibleId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Архив скачан!");
    } catch (error) {
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="tg-onesix-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            TG CointPay
          </h1>
          <p className="text-slate-500 mt-1">
            Telegram бот обмена криптовалюты
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchProfiles(); fetchBotUsers(); }} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Обновить
          </Button>
          <a href={botSettings.bot_link || 'https://t.me/cointvault_bot'} target="_blank" rel="noopener noreferrer">
            <Button variant="outline">
              <Bot className="w-4 h-4" />
              Открыть бота
            </Button>
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button 
          variant={activeTab === 'bot' ? 'default' : 'outline'}
          onClick={() => setActiveTab('bot')}
          className={activeTab === 'bot' ? 'bg-purple-500' : ''}
        >
          <Bot className="w-4 h-4 mr-2" />
          Управление ботом
        </Button>
        <Button 
          variant={activeTab === 'users' ? 'default' : 'outline'}
          onClick={() => { setActiveTab('users'); fetchBotUsers(); }}
          className={activeTab === 'users' ? 'bg-green-500' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Пользователи ({userStats.total})
        </Button>
        <Button 
          variant={activeTab === 'generator' ? 'default' : 'outline'}
          onClick={() => setActiveTab('generator')}
          className={activeTab === 'generator' ? 'bg-blue-500' : ''}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Генератор шаблонов
        </Button>
      </div>

      {/* Bot Management Tab */}
      {activeTab === 'bot' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bot Status Card */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Статус бота
              </h2>
              
              {/* Status indicator */}
              <div className={`p-4 rounded-xl mb-6 ${botStatus.is_running ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${botStatus.is_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className={`font-medium ${botStatus.is_running ? 'text-green-700' : 'text-red-700'}`}>
                      {botStatus.is_running ? 'Бот работает' : 'Бот остановлен'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {botStatus.is_running ? (
                      <Button onClick={stopBot} variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50">
                        <Square className="w-4 h-4 mr-1" />
                        Остановить
                      </Button>
                    ) : (
                      <Button onClick={startBot} size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                        <Play className="w-4 h-4 mr-1" />
                        Запустить
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Active Profile */}
              <div className="space-y-3 mb-6">
                <Label className="text-slate-700">Активный профиль для бота</Label>
                <div className="relative">
                  <button
                    onClick={() => setBotProfileDropdown(!botProfileDropdown)}
                    className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300"
                  >
                    {botStatus.active_profile ? (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-slate-800 font-medium">{botStatus.active_profile.name}</div>
                          <div className="text-xs text-slate-500">
                            {(botStatus.active_profile.balance || 0).toLocaleString()} ₽
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400">Выберите профиль...</span>
                    )}
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${botProfileDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {botProfileDropdown && (
                    <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                      {profiles.map((profile) => (
                        <button
                          key={profile._id || profile.id}
                          onClick={() => setActiveProfile(profile)}
                          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50"
                        >
                          <User className="w-4 h-4 text-slate-500" />
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.accountName || profile.name}</div>
                            <div className="text-xs text-slate-500">
                              {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Bot Link */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="text-sm text-slate-600 mb-2">Ссылка на бота:</div>
                <div className="flex items-center gap-2">
                  <input 
                    type="text"
                    value={botSettings.bot_link || ''}
                    onChange={(e) => setBotSettings({...botSettings, bot_link: e.target.value})}
                    className="flex-1 p-2 bg-white rounded border text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="https://t.me/your_bot"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    navigator.clipboard.writeText(botSettings.bot_link || '');
                    toast.success('Ссылка скопирована!');
                  }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <a href={botSettings.bot_link || '#'} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-slate-400 mt-2">Укажите ссылку на вашего Telegram бота</p>
              </div>
            </CardContent>
          </Card>

          {/* Bot Settings Card */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Настройки отображения
              </h2>

              {/* Info about how it works */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-blue-800 mb-1">Как это работает</div>
                    <div className="text-sm text-blue-600">
                      Все пользователи бота будут видеть данные из выбранного вами OZON профиля. 
                      Баланс, операции и статус верификации берутся из профиля выше.
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Welcome Message */}
              <div className="space-y-3 mb-6">
                <Label className="text-slate-700">Приветственное сообщение</Label>
                <textarea 
                  value={botSettings.welcome_message || ''}
                  onChange={(e) => setBotSettings({...botSettings, welcome_message: e.target.value})}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white min-h-[100px] focus:border-blue-500 focus:outline-none"
                  placeholder="Текст приветствия при команде /start"
                />
              </div>

              {/* Verified Default */}
              <div className="mb-6">
                <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-blue-300 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={botSettings.verified_default !== false}
                    onChange={(e) => setBotSettings({...botSettings, verified_default: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-blue-500"
                  />
                  <div>
                    <div className="font-medium text-slate-700">Верифицированный по умолчанию</div>
                    <div className="text-xs text-slate-500">Показывать статус "Верифицирован" для всех пользователей</div>
                  </div>
                </label>
              </div>

              <Button 
                onClick={saveBotSettings} 
                disabled={isSavingBot}
                className="w-full bg-purple-500 hover:bg-purple-600"
              >
                {isSavingBot ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Сохранить настройки
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{userStats.total}</div>
                    <div className="text-sm text-slate-500">Всего пользователей</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{userStats.today}</div>
                    <div className="text-sm text-slate-500">Сегодня</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <History className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-800">{userStats.this_week}</div>
                    <div className="text-sm text-slate-500">За неделю</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">Пользователи бота</h2>
                <Button onClick={fetchBotUsers} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Обновить
                </Button>
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <p className="text-sm text-blue-700">
                  💡 Назначьте каждому пользователю OZON профиль. Пользователь будет видеть данные только своего профиля.
                </p>
              </div>
              
              {botUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Пока нет пользователей</p>
                  <p className="text-sm text-slate-400 mt-2">Пользователи появятся после того, как напишут боту</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Пользователь</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Назначенный профиль</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Последний визит</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {botUsers.map((user, i) => (
                        <UserRow 
                          key={user.user_id} 
                          user={user} 
                          profiles={profiles} 
                          onUpdate={fetchBotUsers} 
                          onDelete={(userId) => setBotUsers(prev => prev.filter(u => u.user_id !== userId))}
                          index={i}
                          adminIds={botSettings.admin_ids || []}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generator Tab */}
      {activeTab === 'generator' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Page Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите страницу</Label>
              <div className="grid grid-cols-2 gap-3">
                {PAGES.map((page) => {
                  const Icon = page.icon;
                  return (
                    <button
                      key={page.id}
                      onClick={() => { setSelectedPage(page.id); setGeneratedData(null); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedPage === page.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${selectedPage === page.id ? 'text-blue-500' : 'text-slate-400'}`} />
                      <div className={`font-medium ${selectedPage === page.id ? 'text-blue-700' : 'text-slate-700'}`}>
                        {page.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{page.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Verification Status */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-blue-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={isVerified} onChange={(e) => { setIsVerified(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                <div>
                  <div className="font-medium text-slate-700">Верифицированный аккаунт</div>
                  <div className="text-xs text-slate-500">Показать полную верификацию KYC</div>
                </div>
              </label>
            </div>

            {/* Random Operations */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-blue-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={addRandomOps} onChange={(e) => { setAddRandomOps(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">3-6 случайных операций + первое пополнение</div>
                </div>
              </label>
            </div>

            {/* Browser Frame Toggle */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-blue-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={showBrowserFrame} onChange={(e) => { setShowBrowserFrame(e.target.checked); setGeneratedData(null); }}
                  className="w-5 h-5 rounded border-slate-300 text-blue-500 focus:ring-blue-500" />
                <div>
                  <div className="font-medium text-slate-700">Рамка Telegram</div>
                  <div className="text-xs text-slate-500">Показать заголовок Telegram и статус-бар</div>
                </div>
              </label>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.accountName || selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.visibleId} • {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">Нет профилей</div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile._id || profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?._id === profile._id || selectedProfile?.id === profile.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.accountName || profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.visibleId} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽
                            </div>
                          </div>
                          {(selectedProfile?._id === profile._id || selectedProfile?.id === profile.id) && (
                            <Check className="w-4 h-4 text-blue-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
              data-testid="generate-btn"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Сгенерировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Preview */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр данных</h2>
            
            {generatedData ? (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Имя:</span>
                    <span className="text-slate-800 font-medium">{generatedData.fullName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Телефон:</span>
                    <span className="text-slate-800">{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Email:</span>
                    <span className="text-slate-800">{generatedData.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Username:</span>
                    <span className="text-slate-800">{generatedData.username}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Балансы:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-green-600">USDT:</span> ${generatedData.usdtBalance}</div>
                      <div><span className="text-blue-600">RUB:</span> {parseFloat(generatedData.balance).toLocaleString()} ₽</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Операций:</span>
                    <span className="text-slate-800 ml-2">{generatedData.transactions.length}</span>
                    <span className="text-green-600 ml-2">
                      (пополнений: {generatedData.transactions.filter(t => t.type === 'deposit').length})
                    </span>
                    <span className="text-red-500 ml-2">
                      (выводов: {generatedData.transactions.filter(t => t.type === 'withdrawal').length})
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Верификация:</span>
                    <span className={`ml-2 font-medium ${generatedData.verified ? 'text-green-600' : 'text-yellow-600'}`}>
                      {generatedData.verified ? '✓ Подтверждена' : '○ Не подтверждена'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={openPreview} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white" data-testid="open-preview-btn">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Открыть
                  </Button>
                  <Button onClick={copyLink} variant="outline" className="flex-1">
                    <Copy className="w-4 h-4 mr-2" />
                    Копировать
                  </Button>
                </div>
                <Button onClick={downloadArchive} disabled={isDownloadingArchive} variant="outline" className="w-full" data-testid="download-archive-btn">
                  {isDownloadingArchive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Скачать архив всех страниц
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500">Выберите профиль и нажмите «Сгенерировать»</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
